import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { CheckoutDialog } from './CheckoutDialog';
import type { CheckoutDialogProviderProps } from './CheckoutDialog.types';
import { useAuth } from '@shared/components/AuthProvider';
import type { CheckoutServiceFacade } from '@apps/registry/guest/components/CheckoutDialog/machines';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useGuestRegistryTelemetry } from '../../GuestRegistry.telemetry';
import { useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import { CheckoutDialogContextProvider } from './CheckoutDialog.context';
import type { CheckoutDialogContextValue } from './CheckoutDialog.context';

const CheckoutDialogProvider = ({ children, openToast, ...dialogProps }: CheckoutDialogProviderProps) => {
  const { ecardPurchaseConfirmationViewed } = useGuestRegistryTelemetry();
  const { getValueString } = useQueryParamHelper();
  const eCardPrice = useGiftWrapECardPrice();
  const [registryItemId, setRegistryItemId] = useState<string | undefined>('');
  const [currentOrderId, setCurrentOrderId] = useState<string | undefined>('');
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [isGiftWrapPurchased, setIsGiftWrapPurchased] = useState(false);
  const [isGroupGifting, setIsGroupGifting] = useState(false);
  const [currentStep, setCurrentStep] = useState<CheckoutServiceFacade['route']>('setup');
  const [donationAmount, setDonationAmount] = useState<number>(0);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    currentUser: { profile },
  } = useAuth();

  const { registryState, eventId, eventHandle } = dialogProps;
  const standaloneRegistriesList = useMemo(() => {
    return registryState?.registry?.filter(registry => registry.id === registryState?.eventId)[0];
  }, [registryState?.registry, registryState?.eventId]);

  const mySessionEmail = standaloneRegistriesList?.mySession?.email;

  const handleOnOpenCheckoutDialog = useCallback<CheckoutDialogContextValue['openCheckoutDialog']>(
    ({ registryItemId, currentOrderId, isAffiliate, isGroupGiftingEnabled, donationAmount, isGiftWrapPurchased }) => {
      setRegistryItemId(registryItemId);
      setCurrentOrderId(currentOrderId);
      setIsAffiliate(!!isAffiliate);
      setIsGiftWrapPurchased(!!isGiftWrapPurchased);
      setIsGroupGifting(!!isGroupGiftingEnabled);
      setDonationAmount(donationAmount || 0);
      onOpen();
    },
    [onOpen],
  );

  useEffect(() => {
    const pcId = getValueString('pcId');
    const orderId = getValueString('order_id');
    const itemId = getValueString('item_id');
    const itemName = getValueString('item_name');
    const itemPrice = getValueString('item_price');
    const isAffiliate = getValueString('is_affiliate') === 'true';

    if (orderId && itemId && !!registryState?.registry) {
      handleOnOpenCheckoutDialog({
        isAffiliate: isAffiliate,
        isGiftWrapPurchased: true,
        currentOrderId: orderId,
        registryItemId: itemId,
      });
      ecardPurchaseConfirmationViewed({
        label: isAffiliate ? 'affiliate' : 'cash',
        pcId: pcId!,
        giftName: itemName || '',
        price: parseFloat(itemPrice || ''),
        eCardPrice,
      });
    }
  }, [onOpen, handleOnOpenCheckoutDialog, registryState?.registry, getValueString, ecardPurchaseConfirmationViewed, eCardPrice]);

  const disableCloseOnOutsideClick = ['paperCheckoutConfirmAndNotify', 'externalCheckoutConfirmAndNotify'].includes(currentStep ?? 'setup');

  const ctx = useMemo<CheckoutDialogContextValue>(
    () => ({
      isOpen,
      registryState,
      registryItemId,
      currentOrderId,
      profile,
      mySessionEmail,
      isAffiliate,
      isGroupGifting,
      openCheckoutDialog: handleOnOpenCheckoutDialog,
      disableCloseOnOutsideClick,
      setCurrentStep,
      openToast,
    }),
    [
      isOpen,
      currentOrderId,
      disableCloseOnOutsideClick,
      handleOnOpenCheckoutDialog,
      isAffiliate,
      isGroupGifting,
      mySessionEmail,
      profile,
      registryItemId,
      registryState,
      openToast,
    ],
  );

  return (
    <CheckoutDialogContextProvider value={ctx}>
      {children}

      <CheckoutDialog
        eventId={eventId || ''}
        eventHandle={eventHandle}
        isOpen={isOpen && !!registryItemId}
        onClose={onClose}
        registryItemId={registryItemId}
        currentOrderId={currentOrderId}
        isAffiliate={isAffiliate}
        isGiftWrapPurchased={isGiftWrapPurchased}
        isGroupGifting={isGroupGifting}
        donationAmount={donationAmount}
        {...dialogProps}
      />
    </CheckoutDialogContextProvider>
  );
};

export { CheckoutDialogProvider };
export { useCheckoutDialogContext } from './CheckoutDialog.context';
